import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const columns = [
  { label: 'Company Name', key: 'company_name' },
  { label: 'Address', key: 'address' },
  { label: 'PAN', key: 'pancard' },
  { label: 'City', key: 'city' },
  { label: 'State', key: 'state' },
  { label: 'Country', key: 'country' },
  { label: 'GST Number', key: 'gst_number' },
  { label: 'GST UIN', key: 'gst_uin' },
  { label: 'Code', key: 'code' },
  { label: 'CIN', key: 'cin' },
  { label: 'Email', key: 'email' },
];

// Add the Company interface
interface Company {
  id: number | string;
  company_name?: string;
  address?: string;
  pancard?: string;
  city?: string;
  state?: string;
  country?: string;
  gst_number?: string;
  gst_uin?: string;
  code?: string;
  cin?: string;
  email?: string;
  [key: string]: any; // Allow for index signature
}

const CompanyPage = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [deletingId, setDeletingId] = useState<number | string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const navigate = useNavigate();

  const fetchCompanies = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/auth/companies`);
      setCompanies(res.data);
    } catch (err) {
      setError('Error loading companies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleAdd = () => {
    navigate('/addcompany/?company_id=new');
  };

  const handleEdit = (id: number | string) => {
    navigate(`/addcompany/?company_id=${id}`);
  };

  const handleDelete = async (id: number | string) => {
    setDeleteError('');
    const confirmed = window.confirm('Are you sure you want to delete this company? This will also delete all related bank details.');
    if (!confirmed) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API_URL}/auth/company/${id}`);
      setCompanies((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      setDeleteError('Error deleting company');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Companies</h2>
        <button
          onClick={handleAdd}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
        >
          Add Company
        </button>
      </div>
      {error && <div className="text-red-600 mb-2">{error}</div>}
      {deleteError && <div className="text-red-600 mb-2">{deleteError}</div>}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white shadow rounded">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="py-2 px-4 border-b bg-gray-100 text-left text-sm font-medium"
                >
                  {col.label}
                </th>
              ))}
              <th className="py-2 px-4 border-b bg-gray-100 text-left text-sm font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length + 1} className="text-center py-6">Loading...</td>
              </tr>
            ) : companies.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="text-center py-6">No companies found.</td>
              </tr>
            ) : (
              companies.map((company) => (
                <tr key={company.id} className="hover:bg-gray-50 transition">
                  {columns.map((col) => (
                    <td key={col.key} className="py-2 px-4 border-b text-sm">
                      {company[col.key] || <span className="text-gray-400">-</span>}
                    </td>
                  ))}
                  <td className="py-2 px-4 border-b">
                    <button
                      className="px-3 py-1 mr-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition text-xs"
                      onClick={() => handleEdit(company.id)}
                      disabled={deletingId === company.id}
                    >
                      Edit
                    </button>
                    <button
                      className={`px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition text-xs ${deletingId === company.id ? 'opacity-60 cursor-not-allowed' : ''}`}
                      onClick={() => handleDelete(company.id)}
                      disabled={deletingId === company.id}
                    >
                      {deletingId === company.id ? 'Deleting...' : 'Delete'}
                    </button>
                    <button
                      className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition text-xs"
                      onClick={() => window.location.href = `/addBank?company_id=${company.id}`}
                    >
                      Bank Details
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CompanyPage;